import type { MedplumRequestOptions } from '@medplum/core'
import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import {
  getSearchKey,
  normalizeQuery,
  type QueryTypes,
} from '@seen/core/search'
import {
  skipToken,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query'
import type { ConfigurableFhirQuery } from '#src/components/core/FhirQueryLoader.tsx'
import { NO_QUERY } from '#src/constants/no-query.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useErrorToast } from '#src/hooks/useErrorToast.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

export interface UseFhirSearchOneOptions<T extends ResourceType> {
  search?: MedplumRequestOptions
  query?: Omit<
    UseQueryOptions<
      ExtractResource<T> | null,
      Error,
      ExtractResource<T> | null,
      ReturnType<(typeof cacheKeys)['searchOne']>
    >,
    'initialData' | 'queryKey' | 'queryFn' | 'keepPreviousData'
  >
}

/**
 * @param type - The resource type.
 * @param query - The query.
 * @param options - The options.
 * @returns The query result.
 * @deprecated Use useSearchOne instead
 */
export function useFhirSearchOne<T extends ResourceType>(
  type: T,
  query: QueryTypes | undefined,
  options: UseFhirSearchOneOptions<T> = {}
): ConfigurableFhirQuery<UseQueryResult<ExtractResource<T> | null>, true> {
  const medplum = useMedplum()
  const parameters = normalizeQuery(query ? { ...query, _count: 1 } : undefined) // Even though medplum.searchOne automatically adds _count=1 (https://github.com/medplum/medplum/blob/df3fe74c9372c336ff7163591eed96aebf4306b5/packages/core/src/client.ts#L1523), we need to explicitly add it here to avoid caching issues (i.e. stomping over useFhirSearch cache results with the same query). Also, this way if someone happens to call useFhirSearch with _count=1, it'll hit the cache if useFhirSearchOne was called beforehand.

  const searchKey = query ? getSearchKey(medplum, type, parameters) : NO_QUERY

  const queryResult = useQuery({
    ...options?.query,
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: cacheKeys.searchOne(type, searchKey),
    queryFn:
      searchKey === NO_QUERY
        ? skipToken
        : async () => {
            const res = await medplum.searchOne<T>(
              type,
              parameters,
              options.search
            )
            // react-query requires null instead of undefined for their query caching
            return res ?? null
          },
    meta: {
      ...options?.query?.meta,
      queryParams: parameters,
      resourceType: type,
    },
  })

  useErrorToast(
    queryResult.error &&
      `Error accessing ${searchKey}: ${queryResult.error.message ?? 'Unknown error'}`
  )

  return {
    query: queryResult,
    nullable: true,
  }
}
